import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  aiGovernanceApi,
  type AuditEntry,
  type CertificationRecord,
  type CounterfactualScenario,
  type DecisionLineage,
  type EvidenceGraphSnapshot,
  type ExplainabilityReport,
  type GovernanceDashboardSnapshot,
  type RiskAssessment,
  type TransparencySnapshot,
} from '../../api/ai-governance-gov001-runtime';

type Tab = 'dashboard' | 'explain' | 'evidence' | 'lineage' | 'counterfactual' | 'certification' | 'risk' | 'audit' | 'transparency';

const SAMPLE_REC = 'rec-001';

export function AIGovernancePage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<GovernanceDashboardSnapshot | null>(null);
  const [explain, setExplain] = useState<ExplainabilityReport | null>(null);
  const [evidence, setEvidence] = useState<EvidenceGraphSnapshot | null>(null);
  const [lineage, setLineage] = useState<DecisionLineage | null>(null);
  const [counterfactuals, setCounterfactuals] = useState<CounterfactualScenario[]>([]);
  const [certification, setCertification] = useState<CertificationRecord | null>(null);
  const [risk, setRisk] = useState<RiskAssessment | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [transparency, setTransparency] = useState<TransparencySnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-indigo-200 bg-white dark:border-indigo-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-indigo-700 dark:text-indigo-300';
  const shell = 'bg-indigo-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, exp, ev, lin, cf, cert, rk, aud, trans] = await Promise.all([
        aiGovernanceApi.dashboard(), aiGovernanceApi.explain(SAMPLE_REC),
        aiGovernanceApi.evidence(SAMPLE_REC), aiGovernanceApi.lineage(SAMPLE_REC),
        aiGovernanceApi.counterfactuals(SAMPLE_REC), aiGovernanceApi.certification(SAMPLE_REC),
        aiGovernanceApi.risk(SAMPLE_REC), aiGovernanceApi.audit(), aiGovernanceApi.transparency(),
      ]);
      setDashboard(dash); setExplain(exp); setEvidence(ev); setLineage(lin);
      setCounterfactuals(cf); setCertification(cert); setRisk(rk); setAudit(aud); setTransparency(trans);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load AI Governance');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Trust Dashboard' }, { id: 'explain', label: 'Explainability' },
    { id: 'evidence', label: 'Evidence Graph' }, { id: 'lineage', label: 'Decision Lineage' },
    { id: 'counterfactual', label: 'Counterfactuals' }, { id: 'certification', label: 'Certification' },
    { id: 'risk', label: 'Risk Assessment' }, { id: 'audit', label: 'Audit Trail' },
    { id: 'transparency', label: 'Transparency Index' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>GOV-000001 · Enterprise AI Governance & Explainability</p>
            <h1 className="text-xl font-semibold">Trust & Transparency Command Center</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Trust Score <strong className={accent}>{dashboard.trust_score}</strong></span>
                <span>Explainability <strong className={accent}>{dashboard.explainability_pct}%</strong></span>
                <span>Evidence <strong className={accent}>{dashboard.evidence_coverage_pct}%</strong></span>
                <span>Certified <strong className={accent}>{dashboard.certified_count}</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/autonomous-ops" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Autonomous Ops</Link>
            <Link to="/platform/ai-governance-gov001" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading governance platform…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-indigo-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Trust Score', value: dashboard.trust_score },
            { label: 'Explainability', value: `${dashboard.explainability_pct}%` },
            { label: 'Decision Accuracy', value: `${dashboard.decision_accuracy_pct}%` },
            { label: 'Transparency Index', value: dashboard.transparency_index },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'explain' && explain && (
        <div className={`space-y-3 rounded-xl border p-4 ${card}`}>
          <h2 className="font-semibold">Why this recommendation?</h2>
          <p className="text-sm"><strong>Why:</strong> {explain.why}</p>
          <p className="text-sm"><strong>Why now:</strong> {explain.why_now}</p>
          <p className="text-sm"><strong>Why this action:</strong> {explain.why_this_action}</p>
          <p className={`text-sm ${accent}`}>Confidence {Math.round(explain.confidence * 100)}% · Impact: {explain.expected_impact}</p>
          <div><p className={`text-xs uppercase ${muted}`}>Evidence</p>{explain.evidence.map((e) => <p key={e} className="text-sm">• {e}</p>)}</div>
          <div><p className={`text-xs uppercase ${muted}`}>Alternatives</p>{explain.alternatives.map((a) => <p key={a} className="text-sm">• {a}</p>)}</div>
        </div>
      )}

      {tab === 'evidence' && evidence && evidence.nodes.map((n) => (
        <div key={n.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{n.label}</p><span className="text-xs uppercase">{n.category}</span></div>
          <p className={`text-xs ${muted}`}>{n.source} · {n.strength} strength · {n.type}</p>
        </div>
      ))}

      {tab === 'lineage' && lineage && lineage.steps.map((s) => (
        <div key={s.step} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">Step {s.step}: {s.stage}</p><span className={`text-xs ${muted}`}>{s.source}</span></div>
          <p className="text-sm">{s.detail}</p>
        </div>
      ))}

      {tab === 'counterfactual' && counterfactuals.map((c) => (
        <div key={c.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{c.scenario}</p>
          <p className={`text-sm ${accent}`}>{c.outcome}</p>
          <p className={`text-xs ${muted}`}>Confidence {Math.round(c.confidence * 100)}%{c.low_bound ? ` · Range ${c.low_bound}–${c.high_bound}` : ''}</p>
        </div>
      ))}

      {tab === 'certification' && certification && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="text-2xl font-semibold">Score {certification.certification_score}</p>
          <div className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
            {[
              ['Governance', certification.governance_status], ['Evidence', certification.evidence_status],
              ['Validation', certification.validation_status], ['Quality', certification.quality_status],
              ['Safety', certification.safety_status],
            ].map(([label, val]) => (
              <p key={label}><span className={muted}>{label}:</span> <span className="capitalize">{val}</span></p>
            ))}
          </div>
        </div>
      )}

      {tab === 'risk' && risk && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="text-xl font-semibold capitalize">Overall: {risk.overall_risk}</p>
          <div className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
            {[
              ['Financial', risk.financial_risk], ['Inventory', risk.inventory_risk],
              ['Compliance', risk.compliance_risk], ['Operational', risk.operational_risk],
              ['Brand', risk.brand_risk], ['Customer Impact', risk.customer_impact],
            ].map(([label, val]) => (
              <p key={label}><span className={muted}>{label}:</span> <span className="capitalize">{val}</span></p>
            ))}
          </div>
        </div>
      )}

      {tab === 'audit' && audit.map((e) => (
        <div key={e.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold capitalize">{e.action}</p><span className={`text-xs ${muted}`}>{e.actor}</span></div>
          <p className={`text-xs ${muted}`}>{e.recommendation_id}{e.engine ? ` · ${e.engine}` : ''}{e.outcome ? ` · ${e.outcome}` : ''}</p>
        </div>
      ))}

      {tab === 'transparency' && transparency && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Explainability', value: `${transparency.explainability_coverage}%` },
            { label: 'Lineage', value: `${transparency.lineage_coverage}%` },
            { label: 'Evidence', value: `${transparency.evidence_coverage}%` },
            { label: 'Certification', value: `${transparency.certification_coverage}%` },
            { label: 'Risk', value: `${transparency.risk_coverage}%` },
            { label: 'Overall Index', value: transparency.overall_index },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
